import React, { useState, useEffect } from 'react';
import { useGetUsersQuery, useUpdateUserStatusMutation } from '../../services/portal';
import { useNavigate } from 'react-router-dom';
import DataGrid from './DataGrid';
import UserImportDialog from './UsersImport/UserImportDialog';
import { Modal, Button, Tab, Tabs, ButtonGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faFileImport } from '@fortawesome/free-solid-svg-icons';
import { isAdmin } from '../../utils/authUtils';

const UsersPage = () => {
  const [activeKey, setActiveKey] = useState('active'); // State to track active tab
  const navigate = useNavigate();
  const { data: users, refetch } = useGetUsersQuery({ forceRefetch: true });
  const [updateUserStatus] = useUpdateUserStatusMutation();
  const [selectedUserOrganization, setSelectedUserOrganization] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(0);
  const [selectedUserFullName, setSelectedUserFullName] = useState('');
  const [selectedUserStatus, setSelectedUserStatus] = useState(false);
  const [userBlockModalShow, setUserBlockModalShow] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [modalAction, setModalAction] = useState(''); // 'disable' or 'activate'
  const [showImportDialog, setShowImportDialog] = useState(false);

  useEffect(() => {
    filterUsers();
  }, [users]);

  const handleUserNew = () => {
    navigate('/users/new');
  };

  const handleImportUsers = () => {
    setShowImportDialog(true);
  };

  /**
   * Refresh the users list after successful import.
   * Also trigger a re-filter to update the active/blocked lists
   * @param results
   */
  const handleImportComplete = (results) => {
    refetch();

    setTimeout(() => {
      filterUsers();
    }, 100);
  };

  const handleImportDialogClose = () => {
    setShowImportDialog(false);
  };

  const handleEditRow = (rowData) => {
    navigate(`/users/${rowData.user_id}`);
  };

  const handleDeleteRow = (rowData, action) => {
    const { user_id, organization, active } = rowData;
    setSelectedUserId(user_id);
    setSelectedUserOrganization(organization.organization_name);
    setSelectedUserFullName(`${rowData.first_name} ${rowData.last_name}`);
    setSelectedUserStatus(active);
    setModalAction(action);
    setUserBlockModalShow(true);
  };

  const handleUserBlockModalCancel = () => {
    setUserBlockModalShow(false);
  };

  const handleUpdateUserStatus = () => {
    updateUserStatus({
      user_id: selectedUserId,
      status: modalAction === 'disable' ? false : true,
    })
      .unwrap()
      .then(() => {
        console.debug('User status successfully updated');
        refetch();
        setUserBlockModalShow(false);
      })
      .catch((error) => console.error('Failed to update user status:', error));
  };

  const filterUsers = () => {
    if (!users || !users.users) return;
    const filteredActive = users.users.filter(user => user.active);
    const filteredBlocked = users.users.filter(user => !user.active);
    setActiveUsers(filteredActive);
    setBlockedUsers(filteredBlocked);
  };

  const handleTabSelect = (key) => {
    setActiveKey(key);
  };

  const columns = [
    { field: 'organization.organization_name', header: 'Organization' },
    { field: 'first_name', header: 'First Name' },
    { field: 'last_name', header: 'Last Name' },
    { field: 'phone', header: 'Phone' },
    { field: 'email', header: 'Email' },
    { field: 'organization.company', header: 'Company' },
    { field: 'organization.department', header: 'Department' },
    { field: 'role', header: 'Role' },
  ];

  return (
    <div className="page-inner">
      <header className="page-title-bar">
        <div className="d-flex justify-content-between">
          <h1 className="page-title ml-2 m-0">
            <i className="far fa-building text-muted mr-2"></i>Users
          </h1>
          <div>
            {isAdmin() && (
              <button type="button" className="btn btn-primary mr-2" onClick={handleImportUsers}>
                <FontAwesomeIcon icon={faFileImport} className="mr-2" />
                Import Users
              </button>
            )}
            <button type="button" className="btn btn-primary m-0 align-right" onClick={handleUserNew}>
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              New
            </button>
          </div>
        </div>
      </header>
      <div className="page">
        <Tabs
          id="user-tabs"
          activeKey={activeKey}
          onSelect={handleTabSelect}
          className="mb-3"
        >
          <Tab eventKey="active" title="Active">
            <div className="card p-3">
              <DataGrid
                data={activeUsers}
                columns={columns}
                onEditRow={handleEditRow}
                onDeleteRow={(rowData) => handleDeleteRow(rowData, 'disable')}
                showDelete={true} // Disable button
                showEditRow={true}
              />
            </div>
          </Tab>
          <Tab eventKey="blocked" title="Blocked">
            <div className="card p-3">
              <DataGrid
                data={blockedUsers}
                columns={columns}

                onActive={(rowData) => handleDeleteRow(rowData, 'activate')}

                showActive={true}
              />
            </div>
          </Tab>
        </Tabs>
      </div>

      <Modal
        show={userBlockModalShow}
        onHide={handleUserBlockModalCancel}
        animation={false}
        centered
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>
            <h6>{modalAction === 'disable' ? 'Disable User' : 'Activate User'}</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to {modalAction === 'disable' ? 'disable' : 'activate'}{' '}
            <strong>{selectedUserFullName}</strong> from the{' '}
            <strong>{selectedUserOrganization}</strong> organization?
          </p>
          <hr className="mb-0 mt-0"></hr>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleUserBlockModalCancel}>
            Cancel
          </Button>
          <Button variant={modalAction === 'disable' ? 'danger' : 'success'} onClick={handleUpdateUserStatus}>
            {modalAction === 'disable' ? 'Disable' : 'Activate'}
          </Button>
        </Modal.Footer>
      </Modal>

      <UserImportDialog
        show={showImportDialog}
        onHide={handleImportDialogClose}
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default UsersPage;
